/**
 * Hotel Country Codes API
 *
 * Handles hotel country codes API calls.
 */
import type { CountryCodesData, HotelCountryCodesCBPayload } from '@contracts';
import type { HotelCountryCodesRequestParams } from '../../types/bookingReviewTypes';
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { baseFetch, createApiError, buildCBUrlParamsLite, buildCBHeaders } from './baseApi';

// ============================================
// Error Class
// ============================================

export const HotelCountryCodesError = createApiError('HotelCountryCodesError');

// ============================================
// Response Type
// ============================================

export interface HotelCountryCodesResponse {
  type: 'hotels-country-codes';
  data: CountryCodesData;
  success?: boolean;
  error?: { code: string; message: string };
}

// ============================================
// Raw API Response Type
// ============================================

interface RawResponse {
  success?: boolean;
  countryCodes?: unknown[];
  error?: { code: string; message: string };
}

// ============================================
// Helper Functions
// ============================================

/**
 * Build the CB backend payload with urlParams, headers, and requestBody
 * Uses common builders from baseApi (lite variant - no requestId/idContext)
 */
export function buildCBPayload(): HotelCountryCodesCBPayload {
  return {
    urlParams: buildCBUrlParamsLite(),
    headers: buildCBHeaders(),
    requestBody: { requestMap: { COUNTRYLIST: {} } },
  };
}

// ============================================
// API Function
// ============================================

/**
 * Fetch country codes from the API
 * POST /hotels/country-codes?sessionId=...&userId=...
 */
export async function fetchHotelCountryCodes(
  params: HotelCountryCodesRequestParams,
): Promise<HotelCountryCodesResponse> {
  const emptyData: CountryCodesData = { countryCodes: [] };

  const payload = buildCBPayload();

  const { data, error } = await baseFetch<RawResponse>({
    endpoint: HOTELS_ENDPOINTS.COUNTRY_CODES,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'HotelCountryCodesClient',
  });

  // Network/HTTP error
  if (error) {
    return { type: 'hotels-country-codes', success: false, data: emptyData, error };
  }

  const codesData: CountryCodesData = {
    countryCodes: data?.countryCodes ?? [],
  } as CountryCodesData;

  // API returned error
  if (data?.success === false && data?.error) {
    logger.warn('[HotelCountryCodesClient] API returned error:', data.error);
    return { type: 'hotels-country-codes', success: false, data: codesData, error: data.error };
  }

  // Success
  logger.warn('[HotelCountryCodesClient] Success:', { count: codesData.countryCodes.length });
  return { type: 'hotels-country-codes', success: true, data: codesData };
}
